import type { FC } from 'react';
import { memo } from 'react';

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';

import { MuiBadge, MuiTooltip } from '@shared/ui-kit';

import { NavigationContainer, TabContainer, TabsContainer } from './NavigationTabs.style';

import type { TabsPropsType, TabType } from '@shared/ui-kit/tabs/NavigationTabs/types';

interface LabelContainerProps extends TabType {
  isSmallSize?: boolean;
}

const LabelContainer = ({
  label,
  tooltipText,
  isDisable,
  isHideTooltip,
  badgeCounter,
  isSmallSize,
}: LabelContainerProps) => {
  return (
    <MuiTooltip
      title={tooltipText || ''}
      tooltipWithIcon={!!tooltipText}
      isUseCustomStatement={!isDisable || isHideTooltip}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Typography
          sx={{ textTransform: isSmallSize ? 'uppercase' : 'initial' }}
          variant={isSmallSize ? 't10m' : 't14r'}
        >
          {label}
        </Typography>
        {badgeCounter && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <MuiBadge variant="standard" badgeContent={badgeCounter} />
          </motion.div>
        )}
      </Box>
    </MuiTooltip>
  );
};

export const NavigationTabs: FC<TabsPropsType> = memo(
  ({
    value,
    handleChange,
    tabs,
    minWidthTabs,
    hideTabsOnMobile,
    isHideBorderBottom,
    hideTabsLabel,
    variant = 'green',
    minWidthForScrollable,
    tabsScrollerCustomStyles,
    isSmallSize = false,
    ...props
  }) => {
    const theme = useTheme();
    const isMobileDisplay = useMediaQuery(theme.breakpoints.down('sm'));

    const allProps = (index: number) => {
      return {
        id: `tab-${index}`,
        'aria-controls': `tab-panel-${index}`,
      };
    };

    const getTabIcon = (item: TabType) => {
      return item.icon ? (
        isMobileDisplay && item.isDisable && !item.isHideTooltip ? (
          <MuiTooltip
            title={item.tooltipText || ''}
            tooltipWithIcon
            isUseCustomStatement={item.isHideTooltip}
          >
            {item.icon}
          </MuiTooltip>
        ) : (
          item.icon
        )
      ) : (
        <></>
      );
    };

    const getTabLabel = (item: TabType) => {
      return (hideTabsOnMobile && isMobileDisplay) || hideTabsLabel ? (
        ''
      ) : (
        <LabelContainer {...item} isSmallSize={isSmallSize} />
      );
    };

    return (
      <NavigationContainer>
        <TabsContainer
          {...props}
          value={value}
          isHideBorderBottom={isHideBorderBottom}
          onChange={handleChange}
          aria-label="basic tabs"
          hideTabsLabel={hideTabsLabel}
          tabVariant={variant}
          minWidthForScrollable={minWidthForScrollable}
          variant="scrollable"
          tabsScrollerCustomStyles={tabsScrollerCustomStyles}
          isSmallSize={isSmallSize}
        >
          {tabs?.map((item, index) => (
            <TabContainer
              isHideTab={item.isHideTab}
              wrapped={false}
              tabVariant={variant}
              minWidthTabs={minWidthTabs}
              key={index}
              disabled={item.isDisable}
              isLastTabOnTheRight={item?.isLastTabOnTheRight}
              hideTabsLabel={hideTabsLabel}
              label={getTabLabel(item)}
              icon={getTabIcon(item)}
              hasLabel={!!item.label}
              iconPosition="start"
              isSmallSize={isSmallSize}
              {...allProps(index)}
            />
          ))}
        </TabsContainer>
      </NavigationContainer>
    );
  },
);
